/**
 * Educational Color Wheel Component
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import {
  Book,
  Eye,
  EyeOff,
  Play,
  Pause,
  RotateCcw,
  Info,
  CheckCircle,
} from "lucide-react";
import { colorEngine } from "../../lib/colorEngine";
import { useColorWheelCanvas } from "../../hooks/education/useColorWheelCanvas";

export type EducationalMode =
  | "normal"
  | "learning"
  | "exercise"
  | "demonstration";
export type HarmonyType =
  | "complementary"
  | "analogous"
  | "triadic"
  | "split-complementary"
  | "tetradic"
  | "monochromatic";

interface EducationalColorWheelProps {
  size?: number;
  onColorSelect?: (color: string) => void;
  initialMode?: EducationalMode;
  className?: string;
}

interface HarmonyInfo {
  type: HarmonyType;
  name: string;
  description: string;
  colors: string[];
  angle: number;
}

interface ExerciseState {
  type: "identify-harmony" | "find-complement" | "create-scheme";
  targetHarmony?: HarmonyType;
  userColors: string[];
  isCorrect: boolean;
  feedback: string;
}

export function EducationalColorWheel({
  size = 400,
  onColorSelect,
  initialMode = "normal",
  className = "",
}: EducationalColorWheelProps) {
  const [mode, setMode] = useState<EducationalMode>(initialMode);
  const [selectedColor, setSelectedColor] = useState<string>("#ff0000");
  const [selectedHarmony, setSelectedHarmony] =
    useState<HarmonyType>("complementary");
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [isDemonstrationPlaying, setIsDemonstrationPlaying] = useState(false);
  const [demonstrationStep, setDemonstrationStep] = useState(0);
  const [exercise, setExercise] = useState<ExerciseState>({
    type: "identify-harmony",
    userColors: [],
    isCorrect: false,
    feedback: "",
  });

  const animationRef = useRef<number | undefined>(undefined);

  const colorWheelData = useMemo(() => {
    const colors: string[] = [];
    for (let i = 0; i < 360; i++) {
      const hue = (i / 360) * 360;
      colors.push(`hsl(${hue}, 100%, 50%)`);
    }
    return colors;
  }, []);

  const harmonyInfo = useMemo((): HarmonyInfo => {
    const baseHue = colorEngine.parseColor(selectedColor)?.hsl?.h || 0;

    const harmonies: Record<HarmonyType, Omit<HarmonyInfo, "type">> = {
      complementary: {
        name: "Complementary",
        description:
          "Colors opposite each other on the wheel create maximum contrast",
        colors: [selectedColor, `hsl(${(baseHue + 180) % 360}, 100%, 50%)`],
        angle: 180,
      },
      analogous: {
        name: "Analogous",
        description:
          "Colors next to each other create harmonious, serene combinations",
        colors: [
          `hsl(${(baseHue - 30 + 360) % 360}, 100%, 50%)`,
          selectedColor,
          `hsl(${(baseHue + 30) % 360}, 100%, 50%)`,
        ],
        angle: 30,
      },
      triadic: {
        name: "Triadic",
        description:
          "Three evenly spaced colors create vibrant, balanced schemes",
        colors: [
          selectedColor,
          `hsl(${(baseHue + 120) % 360}, 100%, 50%)`,
          `hsl(${(baseHue + 240) % 360}, 100%, 50%)`,
        ],
        angle: 120,
      },
      "split-complementary": {
        name: "Split Complementary",
        description:
          "A base color with two adjacent to its complement for strong contrast",
        colors: [
          selectedColor,
          `hsl(${(baseHue + 150) % 360}, 100%, 50%)`,
          `hsl(${(baseHue + 210) % 360}, 100%, 50%)`,
        ],
        angle: 150,
      },
      tetradic: {
        name: "Tetradic",
        description:
          "Four colors in two complementary pairs offer rich variety",
        colors: [
          selectedColor,
          `hsl(${(baseHue + 90) % 360}, 100%, 50%)`,
          `hsl(${(baseHue + 180) % 360}, 100%, 50%)`,
          `hsl(${(baseHue + 270) % 360}, 100%, 50%)`,
        ],
        angle: 90,
      },
      monochromatic: {
        name: "Monochromatic",
        description:
          "Variations of a single hue create sophisticated, unified schemes",
        colors: [
          `hsl(${baseHue}, 100%, 30%)`,
          `hsl(${baseHue}, 100%, 50%)`,
          `hsl(${baseHue}, 100%, 70%)`,
        ],
        angle: 0,
      },
    };

    return { type: selectedHarmony, ...harmonies[selectedHarmony] };
  }, [selectedColor, selectedHarmony]);

  // Fixed: Pass all required parameters to the hook
  const { canvasRef: wheelCanvasRef } = useColorWheelCanvas({
    size,
    colorWheelData,
    selectedColor,
    mode,
    harmonyColors: mode === "learning" ? harmonyInfo.colors : [],
  });

  const checkHarmonyAnswer = useCallback(
    (colors: string[], targetHarmony?: HarmonyType): boolean => {
      if (!targetHarmony) return false;

      const hues = colors
        .map((color) => {
          const parsed = colorEngine.parseColor(color);
          return parsed?.hsl?.h ?? 0;
        })
        .sort();

      switch (targetHarmony) {
        case "complementary":
          if (hues.length < 2) return false;
          const diff = Math.abs(hues[1] - hues[0]);
          return diff >= 150 && diff <= 210;
        case "analogous":
          return hues.every((hue, i) => {
            if (i === 0) return true;
            const diff = Math.abs(hue - hues[i - 1]);
            return diff <= 60;
          });
        case "triadic":
          if (hues.length !== 3) return false;
          const diffs = [
            Math.abs(hues[1] - hues[0]),
            Math.abs(hues[2] - hues[1]),
            Math.abs(hues[0] + 360 - hues[2]),
          ];
          return diffs.every((diff) => diff >= 100 && diff <= 140);
        default:
          return false;
      }
    },
    [],
  );

  const handleExerciseColorSelection = useCallback(
    (color: string) => {
      if (exercise.type === "identify-harmony") {
        const newColors = [...exercise.userColors, color].slice(-3);
        setExercise((prev) => ({ ...prev, userColors: newColors }));

        if (newColors.length === 3) {
          const isCorrect = checkHarmonyAnswer(
            newColors,
            exercise.targetHarmony,
          );
          setExercise((prev) => ({
            ...prev,
            isCorrect,
            feedback: isCorrect
              ? "Correct! You identified the harmony."
              : "Try again. Look for colors that work well together.",
          }));
        }
      }
    },
    [
      exercise.type,
      exercise.userColors,
      exercise.targetHarmony,
      checkHarmonyAnswer,
    ],
  );

  const startExercise = useCallback((type: ExerciseState["type"]) => {
    const harmonies: HarmonyType[] = ["complementary", "analogous", "triadic"];
    const targetHarmony =
      harmonies[Math.floor(Math.random() * harmonies.length)];

    setExercise({
      type,
      targetHarmony,
      userColors: [],
      isCorrect: false,
      feedback: `Select colors that create a ${targetHarmony} harmony`,
    });
    setMode("exercise");
  }, []);

  const toggleDemonstration = useCallback(() => {
    setIsDemonstrationPlaying((prev) => !prev);
    if (!isDemonstrationPlaying) {
      setDemonstrationStep(0);
    }
  }, [isDemonstrationPlaying]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLCanvasElement>) => {
      if (!selectedColor) return;

      const parsedColor = colorEngine.parseColor(selectedColor);
      if (!parsedColor?.hsl) return;

      let newHue = parsedColor.hsl.h;
      const step = event.shiftKey ? 15 : 5;

      switch (event.key) {
        case "ArrowLeft":
          newHue = (newHue - step + 360) % 360;
          break;
        case "ArrowRight":
          newHue = (newHue + step) % 360;
          break;
        case "ArrowUp":
          newHue = (newHue + step) % 360;
          break;
        case "ArrowDown":
          newHue = (newHue - step + 360) % 360;
          break;
        case " ":
          event.preventDefault();
          if (mode === "demonstration") {
            toggleDemonstration();
          }
          return;
        default:
          return;
      }

      const newColor = `hsl(${newHue}, 100%, 50%)`;
      setSelectedColor(newColor);
      onColorSelect?.(newColor);

      if (mode === "exercise") {
        handleExerciseColorSelection(newColor);
      }
    },
    [
      selectedColor,
      onColorSelect,
      mode,
      toggleDemonstration,
      handleExerciseColorSelection,
    ],
  );

  useEffect(() => {
    const element = wheelCanvasRef.current;
    if (!element) return;

    element.addEventListener("keydown", handleKeyDown as any);
    return () => element.removeEventListener("keydown", handleKeyDown as any);
  }, [handleKeyDown, wheelCanvasRef]);

  const handleCanvasClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = wheelCanvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const centerX = size / 2;
      const centerY = size / 2;
      const dx = x - centerX;
      const dy = y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance <= size / 2 - 10) {
        let angle = Math.atan2(dy, dx) + Math.PI / 2;
        if (angle < 0) angle += Math.PI * 2;

        const hue = (angle / (Math.PI * 2)) * 360;
        const newColor = `hsl(${hue}, 100%, 50%)`;

        setSelectedColor(newColor);
        onColorSelect?.(newColor);

        if (mode === "exercise") {
          handleExerciseColorSelection(newColor);
        }
      }
    },
    [size, onColorSelect, mode, handleExerciseColorSelection],
  );

  useEffect(() => {
    if (isDemonstrationPlaying && mode === "demonstration") {
      const animate = () => {
        setDemonstrationStep((prev) => {
          const next = (prev + 1) % 360;
          const newColor = `hsl(${next}, 100%, 50%)`;
          setSelectedColor(newColor);
          return next;
        });
        animationRef.current = requestAnimationFrame(animate);
      };

      animationRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [isDemonstrationPlaying, mode]);

  return (
    <div
      className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-6 ${className}`}
    >
      <header>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Educational Color Wheel
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAnnotations(!showAnnotations)}
              className={`p-2 rounded-lg transition-colors ${
                showAnnotations
                  ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-400"
              }`}
              title={showAnnotations ? "Hide annotations" : "Show annotations"}
            >
              {showAnnotations ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            { id: "normal" as EducationalMode, label: "Normal", icon: null },
            {
              id: "learning" as EducationalMode,
              label: "Learning",
              icon: Book,
            },
            {
              id: "exercise" as EducationalMode,
              label: "Exercise",
              icon: CheckCircle,
            },
            {
              id: "demonstration" as EducationalMode,
              label: "Demo",
              icon: Play,
            },
          ].map(({ id, label, icon }) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                mode === id
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {icon && React.createElement(icon, { className: "w-4 h-4" })}
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <canvas
              ref={wheelCanvasRef}
              width={size}
              height={size}
              onClick={handleCanvasClick}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              className="rounded-lg shadow-lg cursor-crosshair border-2 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              role="application"
              aria-label="Interactive color wheel. Use arrow keys to navigate, space to select or toggle demonstration."
              aria-description={`Current color: ${selectedColor}. Mode: ${mode}.`}
            />

            {showAnnotations && mode === "learning" && (
              <div className="absolute top-2 left-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 max-w-xs">
                <h4 className="font-semibold text-sm mb-1">
                  {harmonyInfo.name}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {harmonyInfo.description}
                </p>
              </div>
            )}
          </div>

          {selectedColor && (
            <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <div
                className="w-12 h-12 rounded-lg border-2 border-gray-300 dark:border-gray-600"
                style={{ backgroundColor: selectedColor }}
              />
              <div>
                <div className="font-mono text-sm font-medium">
                  {selectedColor}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {colorEngine.parseColor(selectedColor)?.hsl?.h.toFixed(0)}°
                  hue
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {mode === "learning" && (
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
                Select Harmony Type
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    type: "complementary" as HarmonyType,
                    label: "Complementary",
                  },
                  { type: "analogous" as HarmonyType, label: "Analogous" },
                  { type: "triadic" as HarmonyType, label: "Triadic" },
                  {
                    type: "split-complementary" as HarmonyType,
                    label: "Split Comp",
                  },
                  { type: "tetradic" as HarmonyType, label: "Tetradic" },
                  {
                    type: "monochromatic" as HarmonyType,
                    label: "Monochromatic",
                  },
                ].map(({ type, label }) => (
                  <button
                    key={type}
                    onClick={() => setSelectedHarmony(type)}
                    className={`px-3 py-2 text-xs font-medium rounded transition-colors ${
                      selectedHarmony === type
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="space-y-2">
                <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Resulting Colors:
                </h5>
                <div className="flex gap-2">
                  {harmonyInfo.colors.map((color, index) => (
                    <div key={index} className="text-center">
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-gray-300 dark:border-gray-600 mb-1"
                        style={{ backgroundColor: color }}
                      />
                      <div className="text-xs font-mono">{color}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {mode === "exercise" && (
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
                Color Harmony Exercise
              </h4>

              <div className="space-y-2">
                <button
                  onClick={() => startExercise("identify-harmony")}
                  className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Start New Exercise
                </button>
              </div>

              {exercise.feedback && (
                <div
                  className={`p-3 rounded-lg text-sm ${
                    exercise.isCorrect
                      ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                      : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                  }`}
                >
                  {exercise.feedback}
                </div>
              )}

              {exercise.userColors.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Your selections:
                  </h5>
                  <div className="flex gap-2">
                    {exercise.userColors.map((color, index) => (
                      <div
                        key={index}
                        className="w-10 h-10 rounded border-2 border-gray-300 dark:border-gray-600"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {mode === "demonstration" && (
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
                Color Wheel Demonstration
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Watch as we explore the color wheel automatically, showing how
                colors relate to each other.
              </p>

              <div className="flex gap-2">
                <button
                  onClick={toggleDemonstration}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isDemonstrationPlaying
                      ? "bg-red-600 hover:bg-red-700 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {isDemonstrationPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {isDemonstrationPlaying ? "Pause" : "Play"}
                </button>
                <button
                  onClick={() => setDemonstrationStep(0)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400">
                Current position: {demonstrationStep}°
              </div>
            </div>
          )}

          {mode !== "normal" && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>Tip:</strong> Click anywhere on the color wheel to
                  select a color. In learning mode, harmony relationships are
                  automatically visualized with connecting lines.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
